import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import { Link } from 'foundations-components/transitional/components/Link';
import { Fragment, useEffect, useRef } from 'react';
import { PublicBannerSection } from '../../components/banners/PublicBannerSection';
import { PublicBannerSkeleton } from '../../components/banners/PublicBannerSkeleton';
import { COMMERCE_PAGE } from '../../components/error-boundary/ErrorBoundaryNames';
import { PublicFaqsSection } from '../../components/faqs/PublicFaqsSection';
import { LoadablePublicProductFeatureTable } from '../../components/feature-table/PublicProductFeatureTable.loadable';
import { PublicCalculatorAnchorLink } from '../../components/header/PublicCalculatorAnchorLink';
import { PublicHubPageHeader } from '../../components/header/PublicHubPageHeader';
import { LoadablePublicHubCreditsExplainer } from '../../components/hub-credits-explainer/PublicHubCreditsExplainer.loadable';
import { PublicIntegrationsSection } from '../../components/integrations/PublicIntegrationsSection';
import { PublicHubMerchCards } from '../../components/merch-card/PublicHubMerchCards';
import { PublicPaymentsProcessorsSection } from '../../components/commerce-hub/PublicPaymentsProcessorsSection';
import { PublicPageWrapper } from '../../components/PublicPageWrapper';
import PublicPhoneSupportFooter from '../../components/PublicPhoneSupportFooter';
import { useIsRevenueHubEnabled } from '../../hooks/useIsRevenueHubEnabled';
import { usePublicBannerForPage } from '../../hooks/usePublicBannerForPage';
import { usePublicDocumentTitle } from '../../hooks/usePublicDocumentTitle';
import { usePublicPageUrlState } from '../../hooks/usePublicPageUrlState';
import { usePublicProductGroupsData } from '../../hooks/usePublicProductGroupsData';
import { Screens } from '../../types/PublicNavItem';
import { TRACKING_SOURCES, trackClickedLink, trackPageView } from '../../utils/tracking';
import { PublicSetUpPaymentsCTA } from '../../components/commerce-hub/PublicSetUpPaymentsCTA';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const PRODUCT_GROUP = 'COMMERCE';
const SCREEN = Screens.COMMERCE;
export default function PublicCommerceHubPage() {
  const isRevenueHubEnabled = useIsRevenueHubEnabled();
  const {
    selectedTerm,
    setSelectedTerm,
    isEnterpriseTier
  } = usePublicPageUrlState();
  const {
    rawData,
    cards,
    cardCount,
    status,
    currencyCode,
    professionalApiName,
    enterpriseApiName
  } = usePublicProductGroupsData(PRODUCT_GROUP, {
    selectedTerm,
    isEnterpriseTier
  });
  const {
    hasBanner,
    bannerProps
  } = usePublicBannerForPage(rawData, SCREEN);
  usePublicDocumentTitle(SCREEN);
  const hasTrackedPageViewRef = useRef(false);
  useEffect(() => {
    if (!hasTrackedPageViewRef.current) {
      hasTrackedPageViewRef.current = true;
      trackPageView({
        screen: SCREEN
      });
    }
  }, []);
  return /*#__PURE__*/_jsxs(PublicPageWrapper, {
    errorBoundaryName: COMMERCE_PAGE,
    navMarkerName: "COMMERCE_PAGE_V2",
    metricsRoute: SCREEN,
    chunkName: "public-commerce-hub-page",
    children: [/*#__PURE__*/_jsx(PublicBannerSkeleton, {
      isLoading: status === 'loading',
      children: hasBanner && /*#__PURE__*/_jsx(PublicBannerSection, Object.assign({}, bannerProps))
    }), /*#__PURE__*/_jsx(PublicHubPageHeader, {
      productGroup: PRODUCT_GROUP,
      subtitles: [/*#__PURE__*/_jsxs(Fragment, {
        children: [isRevenueHubEnabled ? /*#__PURE__*/_jsx(FormattedMessage, {
          message: "publicPricingPage.header.revenue.subtitle"
        }) : /*#__PURE__*/_jsx(FormattedMessage, {
          message: "publicPricingPage.header.commerce.subtitle"
        }), professionalApiName && /*#__PURE__*/_jsx(PublicCalculatorAnchorLink, {
          defaultProductApiName: professionalApiName,
          onClick: () => trackClickedLink({
            name: 'calculate-your-price',
            url: '#calculator',
            type: 'internal',
            trackingSource: SCREEN,
            screen: SCREEN
          })
        })]
      }, "subtitle"), isRevenueHubEnabled ? /*#__PURE__*/_jsx(FormattedReactMessage, {
        message: "publicPricingPage.header.revenue.subtitle2",
        options: {
          viewPricingLink: /*#__PURE__*/_jsx(Link, {
            href: "#payments-section",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "publicPricingPage.header.revenue.viewPricingLink"
            })
          })
        }
      }, "subtitle2") : /*#__PURE__*/_jsx(FormattedReactMessage, {
        message: "publicPricingPage.header.commerce.subtitle2",
        options: {
          viewPricingLink: /*#__PURE__*/_jsx(Link, {
            href: "#payments-section",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "publicPricingPage.header.commerce.viewPricingLink"
            })
          })
        }
      }, "subtitle2")]
    }), /*#__PURE__*/_jsx(PublicHubMerchCards, {
      cards: cards,
      productGroup: PRODUCT_GROUP,
      status: status,
      currencyCode: currencyCode,
      selectedTerm: selectedTerm,
      setSelectedTerm: setSelectedTerm,
      skeletonCount: cardCount,
      screen: SCREEN,
      renderSecondaryCTA: card => card.productLevel === 'FREE' ? /*#__PURE__*/_jsx(PublicSetUpPaymentsCTA, {
        use: "secondary",
        block: true,
        screen: SCREEN,
        trackingSource: TRACKING_SOURCES.MERCH_CARD
      }) : undefined
    }), /*#__PURE__*/_jsx(LoadablePublicHubCreditsExplainer, {
      screen: SCREEN,
      cards: cards,
      currencyCode: currencyCode,
      term: selectedTerm,
      professionalApiName: professionalApiName,
      enterpriseApiName: enterpriseApiName
    }), /*#__PURE__*/_jsx(LoadablePublicProductFeatureTable, {
      productGroup: PRODUCT_GROUP,
      productGroupResponse: rawData,
      currencyCode: currencyCode,
      selectedTerm: selectedTerm,
      screen: SCREEN
    }), /*#__PURE__*/_jsx("div", {
      id: "payments-section",
      children: /*#__PURE__*/_jsx(PublicPaymentsProcessorsSection, {
        screen: SCREEN
      })
    }), /*#__PURE__*/_jsx(PublicIntegrationsSection, {
      productGroup: PRODUCT_GROUP,
      screen: SCREEN
    }), /*#__PURE__*/_jsx(PublicFaqsSection, {
      productGroup: PRODUCT_GROUP,
      screen: SCREEN
    }), /*#__PURE__*/_jsx(PublicPhoneSupportFooter, {})]
  });
}